import React from "react";
import { Reveal, Overline } from "@/components/Reveal";
import { PageHero, Section, Button } from "@/components/UI";
import { MapleLogo } from "@/components/Logo";
import { BookOpen, Users, Repeat, Award } from "lucide-react";

const MODULES = [
  { t: "Foundations of Restorative Practice", d: "Skin anatomy, colour theory for restoration, and the distinction between restorative and cosmetic work." },
  { t: "Clinical Safety & IPAC", d: "Infection prevention, single-use protocols, and safe environment standards." },
  { t: "Trauma-Informed Care", d: "Working with patients after cancer, surgery, burns, and congenital difference." },
  { t: "Areola & Nipple Restoration", d: "3D techniques for realistic nipple-areola complex reconstruction post-mastectomy." },
  { t: "Scar & Vitiligo Camouflage", d: "Colour matching, texture, and expectation-setting for camouflage work." },
  { t: "Consent, Ethics & Documentation", d: "Legal and ethical practice, records, and outcome review." },
];

export default function Education() {
  return (
    <>
      <PageHero overline="Education" testId="education-hero"
        title="Education built for a clinical discipline"
        subtitle="Curriculum, faculty, and continuing competency designed to prepare practitioners for restorative work — grounded in safety, ethics, and empathy." />

      <Section className="py-20 lg:py-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {[
            { icon: BookOpen, t: "Structured Curriculum", d: "A defined syllabus mapped to the national standards." },
            { icon: Users, t: "Expert Faculty", d: "Clinicians and experienced restorative practitioners." },
            { icon: Award, t: "Competency-Based", d: "Progress measured by demonstrated skill, not seat time." },
            { icon: Repeat, t: "Continuing Education", d: "Ongoing credits to maintain certification." },
          ].map((f, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <f.icon strokeWidth={1.2} size={32} className="text-ccpm-olive" />
              <h3 className="font-serif text-2xl text-ccpm-charcoal mt-5">{f.t}</h3>
              <p className="mt-3 text-sm text-ccpm-taupe font-light leading-relaxed">{f.d}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <div className="bg-ccpm-stone py-20 lg:py-28">
        <Section>
          <Reveal><Overline>Curriculum Modules</Overline><h2 className="font-serif text-4xl lg:text-5xl text-ccpm-charcoal mt-4 leading-[1.05]">What certified practitioners study</h2></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ccpm-border mt-12 border border-ccpm-border">
            {MODULES.map((m, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08} className="bg-ccpm-ivory p-8">
                <span className="font-serif text-3xl text-ccpm-olive/40">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-xl text-ccpm-charcoal mt-3">{m.t}</h3>
                <p className="mt-3 text-sm text-ccpm-taupe font-light leading-relaxed">{m.d}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      <Section className="py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative overflow-hidden aspect-[4/3] bg-ccpm-olive grain" data-testid="education-slogan-panel">
              <div className="absolute inset-0 flex items-center justify-center">
                <MapleLogo light size={240} className="opacity-[0.10]" />
              </div>
              <div className="absolute inset-0 flex flex-col justify-center px-10 lg:px-14">
                <div className="w-14 h-px bg-ccpm-ivory/40 mb-6" />
                <p className="font-serif text-ccpm-ivory text-3xl lg:text-[2.6rem] leading-[1.12]">
                  Knowledge guides the hand.
                  <span className="block text-ccpm-olive-soft italic mt-1">Compassion guides the practitioner.</span>
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Continuing Competency</Overline>
            <h2 className="font-serif text-4xl text-ccpm-charcoal mt-4 leading-tight">Certification is a commitment, not a certificate.</h2>
            <p className="mt-6 text-ccpm-taupe font-light leading-relaxed">Certified practitioners maintain their standing through annual education credits, peer case review, and re-attestation to the code of ethics — ensuring the standard evolves with the evidence.</p>
            <Button to="/certification" className="mt-8" data-testid="education-cta">View certification pathway</Button>
          </Reveal>
        </div>
      </Section>

      <Section className="pb-20 lg:pb-28">
        <Reveal>
          <div className="bg-ccpm-charcoal text-ccpm-ivory p-10 lg:p-14 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <Overline className="!text-ccpm-olive-soft">Forthcoming Publication</Overline>
              <h2 className="font-serif text-3xl lg:text-4xl mt-4 leading-tight">The national reference textbook is in development</h2>
              <p className="mt-4 text-white/60 font-light max-w-xl">A definitive Canadian text on restorative paramedical micropigmentation — the core resource for CCPM certification. Preview the table of contents.</p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button to="/textbook" variant="light" data-testid="education-textbook">View the textbook</Button>
            </div>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
